import React from 'react';
import PropTypes from 'prop-types';

/**
 * ErrorBoundary catches JavaScript errors in child components and displays
 * a user-friendly fallback UI instead of crashing the entire application.
 *
 * This is a class component because React hooks do not support error boundaries.
 * It wraps the Routes section so the navigation sidebar remains functional
 * even when an error occurs in page content.
 *
 * @example
 * <ErrorBoundary>
 *   <Routes>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *   </Routes>
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * Updates state when an error is thrown in a child component.
   *
   * @param {Error} error - The error that was thrown
   * @returns {Object} New state with hasError set to true
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * Logs error details to the console for debugging purposes.
   *
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Object containing componentStack trace
   */
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem'
        }}>
          <div className="card" style={{ maxWidth: '480px', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              {'\u26A0'}
            </div>
            <h2 style={{ marginBottom: '0.75rem', borderBottom: 'none', paddingBottom: 0 }}>
              Something went wrong
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              An unexpected error occurred while loading this page. You can try
              reloading or navigate back to the dashboard.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                className="btn"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
              <a
                href="/dashboard"
                className="btn btn-outline"
                style={{ textDecoration: 'none' }}
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  /** Child components to wrap with error boundary */
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
